import { useState, useCallback, useMemo } from 'react'
import { Check, Loader2 } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { cn } from '@/lib/utils'

export interface RelationshipItem {
  id: string
  name: string
}

interface RelationshipEditorProps<T extends RelationshipItem> {
  /** Currently selected items */
  currentValues: T[]
  /** Whether this is a multi-select (true) or single-select (false) */
  isMulti: boolean
  /** Callback when an item is added */
  onAdd: (item: RelationshipItem) => void
  /** Callback when an item is removed */
  onRemove: (id: string) => void
  /** Search results data */
  searchData: T[] | undefined
  /** Whether search is loading */
  isSearchLoading: boolean
  /** Current search term (controlled) */
  searchTerm: string
  /** Callback to update search term */
  onSearchChange: (value: string) => void
  /** Placeholder text for search input */
  placeholder?: string
  /** The trigger element */
  children: React.ReactNode
  /** Whether the popover is open (controlled) */
  open: boolean
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void
}

export function RelationshipEditor<T extends RelationshipItem>({
  currentValues,
  isMulti,
  onAdd,
  onRemove,
  searchData,
  isSearchLoading,
  searchTerm,
  onSearchChange,
  placeholder = 'Search...',
  children,
  open,
  onOpenChange,
}: RelationshipEditorProps<T>) {
  const selectedIds = useMemo(
    () => new Set(currentValues.map((v) => v.id)),
    [currentValues]
  )

  const handleSelect = useCallback(
    (item: T) => {
      const isSelected = selectedIds.has(item.id)

      if (isSelected) {
        onRemove(item.id)
      } else {
        if (!isMulti) {
          onOpenChange(false)
        }
        onAdd(item)
      }
    },
    [selectedIds, onAdd, onRemove, isMulti, onOpenChange]
  )

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      onOpenChange(newOpen)
      // Clear search when closing
      if (!newOpen) {
        onSearchChange('')
      }
    },
    [onOpenChange, onSearchChange]
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={searchTerm}
            onValueChange={onSearchChange}
          />
          <CommandList>
            {isSearchLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              </div>
            ) : !searchData || searchData.length === 0 ? (
              <CommandEmpty>No results found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {searchData.map((item) => {
                  const isSelected = selectedIds.has(item.id)
                  return (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={() => handleSelect(item)}
                    >
                      <Check
                        className={cn(
                          'mr-2 size-4',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {item.name}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Hook to manage relationship editor state
 */
export function useRelationshipEditor() {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearch = useDebouncedValue(searchTerm, 300)

  return {
    open,
    setOpen,
    searchTerm,
    setSearchTerm,
    debouncedSearch,
  }
}
